import { applyViewportOffsetClamp } from '@/infrastructure/engine2d/helpers/applyViewportOffsetClamp';
import { Point, type Canvas, type TPointerEvent, type TPointerEventInfo } from 'fabric';
import type { TViewportConstraints } from '@/utils/TViewportConstraints';

type TNotifyZoom = (zoom: number) => void;

// Wheel zoom callback wired into ZoomController. Two-finger touch
// zoom is handled by panCallback via touchZoomState — this callback
// only owns the mouse-wheel path.
function initZoomCallback(
    fabricCanvas: Canvas,
    notifyZoom: TNotifyZoom,
    getConstraints: () => TViewportConstraints,
): (opt: TPointerEventInfo) => void {
    return (opt: TPointerEventInfo) => {
        const { e: wheelEvent }: { e: TPointerEvent } = opt;
        if (!(wheelEvent instanceof WheelEvent)) {
            return;
        }
        wheelEvent.preventDefault();
        wheelEvent.stopPropagation();
        const { clientX, clientY, deltaY } = wheelEvent;
        const zoom = fabricCanvas.getZoom();
        const constraints = getConstraints();
        const { max, min } = constraints.zoomBounds;
        // Clamped, not skipped: the previous range check abandoned the whole
        // gesture whenever the target left the bounds, which left the wheel
        // inert at the default framing instead of stopping at the limit.
        const zoomFactor = Math.min(Math.max(zoom - deltaY / 3000, min), max);
        const zoomCenter = new Point(clientX, clientY);
        fabricCanvas.zoomToPoint(zoomCenter, zoomFactor);
        applyViewportOffsetClamp(fabricCanvas, constraints);
        notifyZoom(fabricCanvas.getZoom());
        fabricCanvas.renderAll();
    };
}

export { initZoomCallback };
export type { TNotifyZoom };
